import React, { useEffect, useRef, useState } from "react";
import { fabric } from "fabric";
import opentype from "opentype.js";
import {
  constrainObjectToDesignAreaBounds,
  getDesignAreaObject,
} from "../utils/designAreaConstraints";

const DEFAULT_FONT_URL = "https://inkox.com/fonts/Cheesecake.ttf";
export const DEFAULT_ARC_CURVE = 2;
const fontCache = new Map();

export const curveTextProps = [
  "id",
  "designElement",
  "isAddedText",
  "isAddedClipart",
  "originalText",
  "fontPath",
  "fontFamily",
  "fill",
  "stroke",
  "strokeWidth",
  "letterSpacing",
  "curve",
  "curveTextSettings",
  "warpType",
  "bend",
  "distortH",
  "distortV",
  "topPercent",
  "leftPercent",
];

const loadFont = (fontUrl) =>
  new Promise((resolve, reject) => {
    const safeUrl = fontUrl || DEFAULT_FONT_URL;

    if (fontCache.has(safeUrl)) {
      resolve(fontCache.get(safeUrl));
      return;
    }

    opentype.load(safeUrl, (err, font) => {
      if (err) {
        reject(err);
        return;
      }

      fontCache.set(safeUrl, font);
      resolve(font);
    });
  });

const getGlyphAdvance = (font, char, fontSize) => {
  const glyph = font.charToGlyph(char);
  if (!glyph || !glyph.advanceWidth) {
    return fontSize * 0.6;
  }

  return (glyph.advanceWidth * fontSize) / font.unitsPerEm;
};

const getKerningAdjustment = (font, leftChar, rightChar, fontSize) => {
  if (!font || !leftChar || !rightChar) {
    return 0;
  }

  const leftGlyph = font.charToGlyph(leftChar);
  const rightGlyph = font.charToGlyph(rightChar);

  return (font.getKerningValue(leftGlyph, rightGlyph) * fontSize) / font.unitsPerEm;
};

const createGlyphPath = (font, char, point, settings) => {
  const glyph = font.charToGlyph(char);
  const advance = getGlyphAdvance(font, char, settings.fontSize);
  const glyphPath = glyph.getPath(-advance / 2, settings.fontSize * 0.35, settings.fontSize);
  const pathData = glyphPath.toPathData(2);

  if (!pathData) {
    return null;
  }

  return new fabric.Path(pathData, {
    left: point.x,
    top: point.y,
    fill: settings.fill,
    stroke: settings.stroke,
    strokeWidth: settings.strokeWidth,
    paintFirst: "stroke",
    originX: "center",
    originY: "center",
    angle: point.angle,
    selectable: false,
    evented: false,
  });
};

const buildCurveTextGroup = (font, settings, position) => {
  const chars = (settings.text || "").split("");
  const totalChars = chars.length;
  const spacingPixels =
    Math.abs(Number(settings.spacing) || 0) <= 1
      ? (Number(settings.spacing) || 0) * settings.fontSize
      : Number(settings.spacing) || 0;
  const glyphAdvances = chars.map((char) => getGlyphAdvance(font, char, settings.fontSize));
  const glyphCenterDistances = [];
  let cursorDistance = 0;
  let textNaturalWidth = 0;

  chars.forEach((char, index) => {
    const advance = glyphAdvances[index];

    if (index > 0) {
      cursorDistance += getKerningAdjustment(font, chars[index - 1], char, settings.fontSize) + spacingPixels;
    }

    glyphCenterDistances.push(cursorDistance + advance / 2);
    cursorDistance += advance;
    textNaturalWidth = cursorDistance;
  });

  const arcDegrees = Math.abs(settings.curve);
  const isStraight = arcDegrees < 1;
  const arcRadians = (arcDegrees * Math.PI) / 180;
  const isFullCircle = arcDegrees >= 359.5;

  if (isFullCircle && totalChars > 1) {
    textNaturalWidth += spacingPixels;
  }

  const textPathLength = Math.max(settings.fontSize, textNaturalWidth);
  const radius = isStraight
    ? 0
    : Math.max(textPathLength / Math.max(arcRadians, 0.01), settings.fontSize * 1.25);

  const getPointOnCurve = (ratio) => {
    if (isStraight) {
      return {
        x: -textPathLength / 2 + ratio * textPathLength,
        y: 0,
        angle: 0,
      };
    }

    const direction = settings.curve < 0 ? 1 : -1;
    const centerAngle = settings.curve < 0 ? -Math.PI / 2 : Math.PI / 2;
    const startAngle =
      settings.curve < 0 ? centerAngle - arcRadians / 2 : centerAngle + arcRadians / 2;
    const currentAngle = startAngle + direction * ratio * arcRadians;
    const tangentAngle =
      (Math.atan2(direction * radius * Math.cos(currentAngle), direction * -radius * Math.sin(currentAngle)) *
        180) /
      Math.PI;

    return {
      x: radius * Math.cos(currentAngle),
      y: radius * Math.sin(currentAngle),
      angle: tangentAngle,
    };
  };

  const glyphObjects = chars
    .map((char, index) => {
      const ratio = totalChars === 1 ? 0.5 : glyphCenterDistances[index] / textPathLength;
      return createGlyphPath(font, char, getPointOnCurve(ratio), settings);
    })
    .filter(Boolean);

  const group = new fabric.Group(glyphObjects, {
    left: position.left,
    top: position.top,
    originX: "left",
    originY: "top",
    selectable: true,
    evented: true,
    subTargetCheck: false,
  });

  group.set({
    ...settings.sourceProps,
    designElement: true,
    isAddedText: true,
    originalText: settings.text,
    fontPath: settings.fontPath,
    fontFamily: settings.fontFamily,
    fill: settings.fill,
    stroke: settings.stroke,
    strokeWidth: settings.strokeWidth,
    letterSpacing: settings.spacing,
    curve: settings.curve,
    curveTextSettings: {
      text: settings.text,
      curve: settings.curve,
      fontSize: settings.fontSize,
      spacing: settings.spacing,
      fill: settings.fill,
      stroke: settings.stroke,
      strokeWidth: settings.strokeWidth,
      fontPath: settings.fontPath,
      fontFamily: settings.fontFamily,
    },
  });

  return group;
};

const getFontSize = (selectedObject) => {
  if (selectedObject?.curveTextSettings?.fontSize) {
    return selectedObject.curveTextSettings.fontSize;
  }

  if (selectedObject?.fontSize) {
    return selectedObject.fontSize;
  }

  return 72;
};

export const normalizeArcCurve = (curve) => {
  const numericCurve = Number(curve);

  if (!Number.isFinite(numericCurve)) {
    return DEFAULT_ARC_CURVE;
  }

  return Math.max(-360, Math.min(360, numericCurve));
};

export const replaceCurveTextObject = async ({
  activeCanvas,
  currentObject,
  updates = {},
  selectedFont,
  selectedFontName,
  selectAfterReplace = true,
}) => {
  if (!activeCanvas || !currentObject) {
    return null;
  }

  const text =
    updates.text ??
    currentObject.originalText ??
    currentObject.curveTextSettings?.text ??
    currentObject.text ??
    "";

  if (!text) {
    return null;
  }

  const fontPath =
    updates.fontPath ||
    currentObject.fontPath ||
    currentObject.curveTextSettings?.fontPath ||
    selectedFont ||
    DEFAULT_FONT_URL;
  const safeFontPath = fontPath.startsWith("http:") ? fontPath.replace("http:", "https:") : fontPath;
  const currentBounds = currentObject.getBoundingRect(true, true);
  const previousCenter = currentObject.getCenterPoint?.();
  const position = {
    left: Number.isFinite(currentBounds.left) ? currentBounds.left : currentObject.left,
    top: Number.isFinite(currentBounds.top) ? currentBounds.top : currentObject.top,
  };
  const objectIndex = activeCanvas.getObjects().indexOf(currentObject);
  const font = await loadFont(safeFontPath);
  const settings = {
    text,
    curve: normalizeArcCurve(updates.curve ?? currentObject.curveTextSettings?.curve ?? currentObject.curve ?? DEFAULT_ARC_CURVE),
    fontSize: Number(updates.fontSize ?? getFontSize(currentObject)) || 72,
    spacing:
      updates.spacing ??
      updates.letterSpacing ??
      currentObject.letterSpacing ??
      currentObject.curveTextSettings?.spacing ??
      0,
    fill: updates.fill ?? currentObject.fill ?? currentObject.curveTextSettings?.fill ?? "#000000",
    stroke: updates.stroke ?? currentObject.stroke ?? currentObject.curveTextSettings?.stroke ?? "",
    strokeWidth: Number(
      updates.strokeWidth ?? currentObject.strokeWidth ?? currentObject.curveTextSettings?.strokeWidth ?? 0
    ),
    fontPath: safeFontPath,
    fontFamily:
      updates.fontFamily ||
      currentObject.fontFamily ||
      currentObject.curveTextSettings?.fontFamily ||
      selectedFontName ||
      "",
    sourceProps: {
      id: currentObject.id,
      warpType: currentObject.warpType,
      bend: currentObject.bend,
      distortH: currentObject.distortH,
      distortV: currentObject.distortV,
      topPercent: currentObject.topPercent,
      leftPercent: currentObject.leftPercent,
    },
  };
  const nextGroup = buildCurveTextGroup(font, settings, position);

  nextGroup.set({
    angle: currentObject.angle || 0,
    scaleX: currentObject.scaleX || 1,
    scaleY: currentObject.scaleY || 1,
    flipX: currentObject.flipX || false,
    flipY: currentObject.flipY || false,
    lockMovementX: currentObject.lockMovementX || false,
    lockMovementY: currentObject.lockMovementY || false,
    lockScalingX: currentObject.lockScalingX || false,
    lockScalingY: currentObject.lockScalingY || false,
    lockRotation: currentObject.lockRotation || false,
  });

  activeCanvas.remove(currentObject);
  if (objectIndex >= 0) {
    activeCanvas.insertAt(nextGroup, objectIndex, false);
  } else {
    activeCanvas.add(nextGroup);
  }

  if (selectAfterReplace) {
    activeCanvas.setActiveObject(nextGroup);
  } else {
    activeCanvas.discardActiveObject();
  }
  nextGroup.setCoords();
  constrainObjectToDesignAreaBounds(
    nextGroup,
    getDesignAreaObject(activeCanvas),
    activeCanvas,
    previousCenter
  );
  activeCanvas.requestRenderAll();

  return nextGroup;
};

const CurveTextComponent = ({
  canvas,
  selectedObject,
  selectedFont,
  selectedFontName,
  activeTab,
  addToHistory,
  calculatePrice,
  saveCanvasDataToLocalStorage,
  onReset,
}) => {
  const [curve, setCurve] = useState(0);
  const activeObjectRef = useRef(null);
  const updateTimerRef = useRef(null);
  const isApplyingCurveRef = useRef(false);
  const pendingCurveRef = useRef(null);

  useEffect(() => {
    if (isApplyingCurveRef.current) {
      activeObjectRef.current = selectedObject || activeObjectRef.current;
      return;
    }

    const selectedCurve = selectedObject?.curveTextSettings?.curve ?? selectedObject?.curve ?? 0;
    setCurve(Number(selectedCurve) || 0);
    activeObjectRef.current = selectedObject || null;
  }, [selectedObject]);

  useEffect(
    () => () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    },
    []
  );

  const applyCurve = async (nextCurve) => {
    const activeCanvas = canvas || window._canvas;
    const currentObject = activeCanvas?.getActiveObject() || activeObjectRef.current;

    if (!activeCanvas || !currentObject) {
      return;
    }

    const text = currentObject.originalText || currentObject.curveTextSettings?.text || currentObject.text || "";
    if (!text) {
      return;
    }

    const prevState = currentObject.toObject(curveTextProps);
    isApplyingCurveRef.current = true;
    pendingCurveRef.current = Number(nextCurve) || 0;

    try {
      const nextGroup = await replaceCurveTextObject({
        activeCanvas,
        currentObject,
        updates: { curve: Number(nextCurve) || 0 },
        selectedFont,
        selectedFontName,
      });

      if (!nextGroup) {
        return;
      }

      activeObjectRef.current = nextGroup;
      setCurve(pendingCurveRef.current);

      const newState = nextGroup.toObject(curveTextProps);
      if (addToHistory) {
        addToHistory(nextGroup, prevState, newState, "curveTextChange");
      }
      if (calculatePrice) {
        calculatePrice(activeCanvas);
      }
      if (saveCanvasDataToLocalStorage && activeTab) {
        saveCanvasDataToLocalStorage(activeTab);
      }
    } catch (error) {
      console.error("Curve text font could not be loaded:", error);
    } finally {
      isApplyingCurveRef.current = false;
      pendingCurveRef.current = null;
    }
  };

  const handleCurveChange = (event) => {
    const nextCurve = Number(event.target.value);
    setCurve(nextCurve);

    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }

    updateTimerRef.current = setTimeout(() => {
      applyCurve(nextCurve);
    }, 120);
  };

  const handleResetCurve = () => {
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
    }

    // Reset the curve value to 0 so the range bar returns to its middle
    // position (0 is the slider's center). The shape type stays selected; use
    // the "None" button to remove the shape entirely.
    setCurve(0);
    applyCurve(0);
  };

  return (
    <div className="arc-slider-row">
      <label className="labFont arc-value-label">Curve</label>
      <input
        className="range-bar arc-range-bar"
        type="range"
        min="-360"
        max="360"
        step="1"
        value={curve}
        onChange={handleCurveChange}
      />
      <button
        type="button"
        className="resetbtn arc-reset-btn"
        onClick={handleResetCurve}
      >
        Reset
      </button>
    </div>
  );
};

export default CurveTextComponent;
